import type { JSX } from 'react';

/**
 * SpaceTree — Phase 3 increment A skeleton.
 *
 * The left-hand navigation column. Renders the hierarchy of Stories
 * in the current Space as a collapsible tree. Phase 3 A ships the
 * static-data version; Phase 3 B wires it to the
 * `/v1/spaces/{slug}/stories?parent_id=…` endpoint from Phase 1
 * increment G + drag-and-drop reordering.
 */
export interface StoryTreeNode {
  uuid: string;
  slug: string;
  full_path: string;
  status: 'draft' | 'published' | 'archived';
  children?: StoryTreeNode[];
}

export interface SpaceTreeProps {
  nodes: StoryTreeNode[];
  selectedPath: string | null;
  onSelect(path: string): void;
}

export function SpaceTree({
  nodes,
  selectedPath,
  onSelect,
}: SpaceTreeProps): JSX.Element {
  if (nodes.length === 0) {
    return (
      <p className="px-2 text-xs text-zinc-500">
        No stories yet. Phase 3 increment B wires the "New story" button.
      </p>
    );
  }

  return (
    <ul className="flex flex-col gap-px">
      {nodes.map((node) => (
        <SpaceTreeNode
          key={node.uuid}
          node={node}
          depth={0}
          selectedPath={selectedPath}
          onSelect={onSelect}
        />
      ))}
    </ul>
  );
}

interface SpaceTreeNodeProps {
  node: StoryTreeNode;
  depth: number;
  selectedPath: string | null;
  onSelect(path: string): void;
}

function SpaceTreeNode({
  node,
  depth,
  selectedPath,
  onSelect,
}: SpaceTreeNodeProps): JSX.Element {
  const isSelected = selectedPath === node.full_path;
  const hasChildren = (node.children?.length ?? 0) > 0;

  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(node.full_path)}
        className={
          'flex w-full items-center gap-2 rounded px-2 py-1 text-left transition ' +
          (isSelected
            ? 'bg-zinc-900 text-white'
            : 'text-zinc-700 hover:bg-zinc-200')
        }
        style={{ paddingLeft: `${depth * 12 + 8}px` }}
        aria-current={isSelected ? 'true' : undefined}
      >
        <span
          aria-hidden="true"
          className="font-mono text-xs"
          title={`Status: ${node.status}`}
        >
          {node.status === 'published' ? '●' : '○'}
        </span>
        <span className="flex-1 truncate">{node.slug || '/'}</span>
      </button>

      {hasChildren && node.children !== undefined ? (
        <ul>
          {node.children.map((child) => (
            <SpaceTreeNode
              key={child.uuid}
              node={child}
              depth={depth + 1}
              selectedPath={selectedPath}
              onSelect={onSelect}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}
